import React, { useRef, useState, useEffect } from 'react';
import Container from '../Container';
import classNames from 'classnames';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { NavigationMenu } from 'radix-ui';
import { Squash as Hamburger } from 'hamburger-react';

import ExploreDeployIcon from '../../assets/icons/explore-deploy.svg';
import BuildDevExperienceIcon from '../../assets/icons/build-dev-experience.svg';
import ExplorePlatform from '../../assets/icons/explore-platform.svg';
import ExploreTestIcon from '../../assets/icons/explore-test.svg';
import ExplorePreviewIcon from '../../assets/icons/explore-previews.svg';
import ExploreAiIcon from '../../assets/icons/explore-ai.svg';

import Button from './Button';

import './Header.scss';

const Header = () => {
  const headerRef = useRef(null);
  const [contentPosition, setContentPosition] = useState(0);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleHover = (e) => {
    const position =
      e.target?.getBoundingClientRect()?.x -
      headerRef?.current?.getBoundingClientRect()?.x -
      50;
    setContentPosition(position);
  };

  const announcementBarQuery = useStaticQuery(graphql`
    query announcementBarQuery {
      sanityAnnouncementBar {
        defaultAnnouncement {
          title
          callToAction {
            url
            label
          }
          image {
            asset {
              url
            }
          }
        }
        scheduledAnnouncement {
          endDateAndTime
          startDateAndTime
          title
          callToAction {
            url
            label
          }
          image {
            asset {
              url
            }
          }
        }
      }
    }
  `);

  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const isDismissed = sessionStorage.getItem('announcementDismissed');
    if (isDismissed === 'true') {
      setIsVisible(false);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.setItem('announcementDismissed', 'true');
    setIsVisible(false);
  };

  return (
    <>
      {isVisible && announcementBarQuery.sanityAnnouncementBar && (
        <AnnouncementBar
          data={announcementBarQuery.sanityAnnouncementBar}
          handleClose={handleClose}
        />
      )}
      <header className="FleetHeader">
        <Container>
          <div className="FleetHeaderContainer">
            <Link href="/" arial-label="Homepage">
              <Logo />
            </Link>
            <div className="FleetHeaderNav" ref={headerRef}>
              <NavigationMenu.Root
                className=""
                style={{ '--content-position': `${contentPosition}px` }}
              >
                <NavigationMenu.List className="FleetHeaderList">
                  <NavigationMenu.Item>
                    <NavigationMenu.Trigger
                      className="FleetHeaderNavTrigger"
                      onMouseEnter={handleHover}
                    >
                      Products
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content className="FleetHeaderNavSubmenu">
                      <ul>
                        <li className="FleetHeaderNavItemWithIcon">
                          <Link
                            href="/ai"
                            className="FleetHeaderNavBlockLink"
                            aria-label="Development Environments"
                          />
                          <ExploreAiIcon />
                          <div>
                            <span className="FleetHeaderNavSubmenuTitle">
                              AI Agent Environments
                              <span className="FleetHeaderNavSubmenuTitleBadge">
                                Beta
                              </span>
                            </span>
                            <span className="FleetHeaderNavSubmenuSubtitle">
                              Dedicated environments to run agent fleets
                            </span>
                          </div>
                        </li>
                        <li className="FleetHeaderNavItemWithIcon">
                          <Link
                            href="/kubernetes-development-environments"
                            className="FleetHeaderNavBlockLink"
                            aria-label="Development Environments"
                          />
                          <ExploreDeployIcon />
                          <div>
                            <span className="FleetHeaderNavSubmenuTitle">
                              Development Environments
                            </span>
                            <span className="FleetHeaderNavSubmenuSubtitle">
                              Modern, Realistic, Self-service on K8s
                            </span>
                          </div>
                        </li>
                        <li className="FleetHeaderNavItemWithIcon">
                          <Link
                            href="/preview-environments"
                            className="FleetHeaderNavBlockLink"
                            aria-label="Preview Environments"
                          />
                          <ExplorePreviewIcon />
                          <div>
                            <span className="FleetHeaderNavSubmenuTitle">
                              Preview Environments
                            </span>
                            <span className="FleetHeaderNavSubmenuSubtitle">
                              Shareable environments with every pull request
                            </span>
                          </div>
                        </li>
                        <li className="FleetHeaderNavItemWithIcon">
                          <Link
                            href="/streamlined-kubernetes-testing"
                            className="FleetHeaderNavBlockLink"
                            aria-label="Test"
                          />
                          <ExploreTestIcon />
                          <div>
                            <span className="FleetHeaderNavSubmenuTitle">
                              Test Environments
                            </span>
                            <span className="FleetHeaderNavSubmenuSubtitle">
                              Run tests directly in your development
                              environments
                            </span>
                          </div>
                        </li>
                        <li className="FleetHeaderNavItemWithIcon">
                          <Link
                            href="/platform-features"
                            className="FleetHeaderNavBlockLink"
                            aria-label="Platform Features"
                          />
                          <ExplorePlatform />
                          <div>
                            <span className="FleetHeaderNavSubmenuTitle">
                              Platform Features
                            </span>
                            <span className="FleetHeaderNavSubmenuSubtitle">
                              Okteto Experience features and benefits
                            </span>
                          </div>
                        </li>
                        <li className="FleetHeaderNavItemWithIcon">
                          <Link
                            href="/automate-dev-on-k8s"
                            className="FleetHeaderNavBlockLink"
                            aria-label="Build Development Experiences"
                          />
                          <BuildDevExperienceIcon />
                          <div>
                            <span className="FleetHeaderNavSubmenuTitle">
                              Build Development Experiences
                            </span>
                            <span className="FleetHeaderNavSubmenuSubtitle">
                              Development experiences for your entire team
                            </span>
                          </div>
                        </li>
                      </ul>
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <NavigationMenu.Link asChild>
                      <Link href="/pricing" className="FleetHeaderNavLink">
                        Pricing
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <NavigationMenu.Link asChild>
                      <Link href="/customers" className="FleetHeaderNavLink">
                        Customers
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <a
                      className="FleetHeaderNavLink"
                      href="https://www.okteto.com/docs/"
                    >
                      Docs
                    </a>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <NavigationMenu.Link asChild>
                      <Link href="/blog" className="FleetHeaderNavLink">
                        Blog
                      </Link>
                    </NavigationMenu.Link>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item>
                    <NavigationMenu.Trigger
                      className="FleetHeaderNavTrigger"
                      onMouseEnter={handleHover}
                    >
                      Resources
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content className="FleetHeaderNavSubmenu">
                      <ul>
                        <li>
                          <Link href="/product-tour-center">Demo Center</Link>
                        </li>
                        <li>
                          <Link href="/videos">Videos</Link>
                        </li>
                        <li>
                          <Link href="/videos/tutorials">Tutorials</Link>
                        </li>
                        <li>
                          <Link href="/videos/webinars">Webinars</Link>
                        </li>
                        <li>
                          <Link href="/devx-principles">DevX Principles</Link>
                        </li>
                        <li>
                          <Link href="/build-versus-buy">Build v. Buy</Link>
                        </li>
                        <li>
                          <Link href="/ebooks-and-guides">
                            eBooks and Guides
                          </Link>
                        </li>
                        <li>
                          <a href="https://community.okteto.com/">Community</a>
                        </li>
                      </ul>
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                </NavigationMenu.List>
              </NavigationMenu.Root>
              <nav className="FleetHeaderNavButtons">
                <Button
                  value={{
                    label: 'Get a demo',
                    url: '/get-demo',
                  }}
                />
              </nav>
              <div className="FleetHeaderNavHamburger">
                <Hamburger
                  size={24}
                  duration={0.2}
                  color="white"
                  toggle={() => setMobileMenuOpen(!mobileMenuOpen)}
                  toggled={mobileMenuOpen}
                />
              </div>
            </div>
            <div
              className={classNames(
                'FleetHeaderMobileNav',
                mobileMenuOpen && 'FleetHeaderMobileNavActive'
              )}
            >
              <ul className="FleetHeaderMobileNavProducts">
                <li className="FleetHeaderNavItemWithIcon">
                  <Link
                    href="/ai"
                    className="FleetHeaderNavBlockLink"
                    aria-label="Development Environments"
                  />
                  <ExploreAiIcon />
                  <div>
                    <span className="FleetHeaderNavSubmenuTitle">
                      AI Agent Environments
                      <span className="FleetHeaderNavSubmenuTitleBadge">
                        Beta
                      </span>
                    </span>
                    <span className="FleetHeaderNavSubmenuSubtitle">
                      Isolated environments to run agent fleets
                    </span>
                  </div>
                </li>
                <li className="FleetHeaderNavItemWithIcon">
                  <Link
                    href="/kubernetes-development-environments"
                    className="FleetHeaderNavBlockLink"
                    aria-label="Development Environments"
                  />
                  <ExploreDeployIcon />
                  <div>
                    <span className="FleetHeaderNavSubmenuTitle">
                      Development Environments
                    </span>
                    <span className="FleetHeaderNavSubmenuSubtitle">
                      Modern, Realistic, Self-service on K8s
                    </span>
                  </div>
                </li>
                <li className="FleetHeaderNavItemWithIcon">
                  <Link
                    href="/preview-environments"
                    className="FleetHeaderNavBlockLink"
                    aria-label="Preview Environments"
                  />
                  <ExplorePreviewIcon />
                  <div>
                    <span className="FleetHeaderNavSubmenuTitle">
                      Preview Environments
                    </span>
                    <span className="FleetHeaderNavSubmenuSubtitle">
                      Shareable environments with every pull request
                    </span>
                  </div>
                </li>
                <li className="FleetHeaderNavItemWithIcon">
                  <Link
                    href="/streamlined-kubernetes-testing"
                    className="FleetHeaderNavBlockLink"
                    aria-label="Test"
                  />
                  <ExploreTestIcon />
                  <div>
                    <span className="FleetHeaderNavSubmenuTitle">
                      Test Environments
                    </span>
                    <span className="FleetHeaderNavSubmenuSubtitle">
                      Run tests directly in your development environments
                    </span>
                  </div>
                </li>
                <li className="FleetHeaderNavItemWithIcon">
                  <Link
                    href="/platform-features"
                    className="FleetHeaderNavBlockLink"
                    aria-label="Platform Features"
                  />
                  <ExplorePlatform />
                  <div>
                    <span className="FleetHeaderNavSubmenuTitle">
                      Platform Features
                    </span>
                    <span className="FleetHeaderNavSubmenuSubtitle">
                      Okteto Experience features and benefits
                    </span>
                  </div>
                </li>
                <li className="FleetHeaderNavItemWithIcon">
                  <Link
                    href="/automate-dev-on-k8s"
                    className="FleetHeaderNavBlockLink"
                    aria-label="Build Development Experiences"
                  />
                  <BuildDevExperienceIcon />
                  <div>
                    <span className="FleetHeaderNavSubmenuTitle">
                      Build Development Experiences
                    </span>
                    <span className="FleetHeaderNavSubmenuSubtitle">
                      Development experiences for your entire team
                    </span>
                  </div>
                </li>
              </ul>
              <div className="FleetHeaderMobileNavSecondary">
                <Link href="/pricing" className="FleetHeaderNavLink">
                  Pricing
                </Link>
                <Link href="/customers" className="FleetHeaderNavLink">
                  Customers
                </Link>
                <a
                  className="FleetHeaderNavLink"
                  href="https://www.okteto.com/docs/"
                >
                  Docs
                </a>
                <Link href="/blog" className="FleetHeaderNavLink">
                  Blog
                </Link>
                <ul className="FleetHeaderMobileNavResources">
                  <li>
                    <Link href="/product-tour-center">Demo Center</Link>
                  </li>
                  <li>
                    <Link href="/videos">Videos</Link>
                  </li>
                  <li>
                    <Link href="/videos/tutorials">Tutorials</Link>
                  </li>
                  <li>
                    <Link href="/videos/webinars">Webinars</Link>
                  </li>
                  <li>
                    <Link href="/devx-principles">DevX Principles</Link>
                  </li>
                  <li>
                    <Link href="/build-versus-buy">Build v. Buy</Link>
                  </li>
                  <li>
                    <Link href="/ebooks-and-guides">eBooks and Guides</Link>
                  </li>
                  <li>
                    <a href="https://community.okteto.com/">Community</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </header>
    </>
  );
};

const Logo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="139"
    height="40"
    viewBox="0 0 139 40"
    fill="none"
  >
    <path
      d="M20 40c11.046 0 20-8.954 20-20S31.046 0 20 0 0 8.954 0 20s8.954 20 20 20Z"
      fill="#00D1CA"
    />
    <path
      d="M13.044 20c0-3.809 3.147-6.957 7.105-6.957a7.143 7.143 0 0 1 5.568 2.636 2.236 2.236 0 1 0 3.474-2.816 11.615 11.615 0 0 0-9.042-4.292c-6.36 0-11.577 5.084-11.577 11.429 0 6.345 5.217 11.428 11.577 11.428 3.652 0 6.92-1.673 9.042-4.291a2.236 2.236 0 1 0-3.474-2.817 7.143 7.143 0 0 1-5.568 2.636c-3.958 0-7.105-3.148-7.105-6.956Z"
      fill="#1A263E"
    />
    <path
      d="M29.193 22.236a2.236 2.236 0 1 0 0-4.472 2.236 2.236 0 0 0 0 4.472Z"
      fill="#1A263E"
    />
    <path
      d="m75.365 29.421-5.702-8.338V29.421h-3.175V3.306h3.175v15.837l5.702-8.234h4.138l-6.446 8.926 6.446 9.586h-4.138Zm12.862 0c-1.385 0-2.533-.44-3.445-1.319-.912-.88-1.38-2.103-1.402-3.672V13.904h-2.978v-2.995h2.978v-4.39h3.185v4.39h4.155v2.995h-4.155v9.456c0 1.165.22 1.955.658 2.371.439.416.935.624 1.49.624.253 0 .501-.03.744-.089.242-.06.467-.137.675-.232l1.004 2.924c-.831.309-1.8.463-2.909.463ZM100.33 10.91c2.645 0 4.519.885 6.314 2.527 1.796 1.642 2.967 4.337 2.755 7.475H94.954c.049 1.721.631 2.7 1.747 3.752 1.116 1.052 2.45 1.578 4.003 1.578 2.135 0 3.921-1.107 4.816-3.155h3.52c-.475 2.093-2.216 4.2-3.643 5.08-1.428.882-3.027 1.255-4.822 1.255-2.62 0-4.78-.884-6.478-2.653-1.698-1.768-2.564-4.172-2.564-6.873 0-2.677.814-4.534 2.464-6.315 1.65-1.78 3.76-2.671 6.332-2.671Zm29.369.03c5.05 0 9.144 4.138 9.144 9.241 0 5.104-4.094 9.241-9.144 9.241s-9.144-4.137-9.144-9.24c0-5.104 4.094-9.242 9.144-9.242Zm-73.944 0c5.05 0 9.144 4.138 9.144 9.241 0 5.104-4.094 9.241-9.144 9.241s-9.144-4.137-9.144-9.24c0-5.104 4.094-9.242 9.144-9.242Zm60.23-4.42v4.39h4.155v2.995h-4.155v9.456c0 1.165.219 1.955.658 2.371.438.416.935.624 1.489.624.254 0 .502-.03.744-.089a3.75 3.75 0 0 0 .675-.232l1.004 2.924c-.831.309-1.8.463-2.908.463-1.385 0-2.533-.44-3.445-1.319-.912-.88-1.379-2.103-1.403-3.672V13.904h-2.977v-2.995h2.977v-4.39h3.186Zm13.714 7.702c-3.257 0-5.897 2.668-5.897 5.96 0 3.29 2.64 5.96 5.897 5.96s5.897-2.67 5.897-5.96c0-3.292-2.64-5.96-5.897-5.96Zm-73.944 0c-3.257 0-5.897 2.668-5.897 5.96 0 3.29 2.64 5.96 5.897 5.96s5.897-2.67 5.897-5.96c0-3.292-2.64-5.96-5.897-5.96Zm44.81-.35a5.459 5.459 0 0 0-3.452 1.248c-.918.772-1.722 1.81-2.016 3.113h10.912a5.456 5.456 0 0 0-1.953-3.113c-1.039-.83-1.948-1.248-3.491-1.248Z"
      fill="#fff"
    />
  </svg>
);

const AnnouncementBar = ({ data, showAnnouncementBar, handleClose }) => {
  const currentDateAndTime = new Date().toISOString();

  const { defaultAnnouncement, scheduledAnnouncement } = data;

  let announcementData = defaultAnnouncement;

  if (
    scheduledAnnouncement &&
    currentDateAndTime > scheduledAnnouncement.startDateAndTime &&
    currentDateAndTime < scheduledAnnouncement.endDateAndTime
  ) {
    announcementData = scheduledAnnouncement;
  }

  if (announcementData?.title) {
    return (
      <div className={classNames('HeaderHighlight HeaderHighlight-show')}>
        <div className="HeaderHighlightWrapper">
          <div className="HeaderHighlightContainer">
            <div className="HeaderHighlightContent">
              <div className="HeaderHighlightTitle">
                {announcementData.image?.asset?.url && (
                  <img src={announcementData.image?.asset?.url} alt="" />
                )}
                {announcementData.title}
              </div>
              <a href={announcementData.callToAction?.url}>
                {announcementData.callToAction?.label} →
              </a>
            </div>
          </div>
          <button
            className="HeaderHighlightClose"
            role="button"
            tabIndex={0}
            onClick={() => handleClose()}
          >
            &#10005;
          </button>
        </div>
      </div>
    );
  }
  return null;
};

export default Header;
